export interface AssistantApproval {
    id: string
    tool?: string
    reason?: string
    settled?: boolean
}

export interface AssistantStreamEvent {
    id: string
    type: string
    delta?: string
    tool_name?: string
    result?: unknown
    successful?: boolean
    error?: string | null
    message?: string
    approvals?: Array<{id?: string, tool?: string, reason?: string | null, arguments?: Record<string, unknown>}>
}

export interface AssistantStreamChunk {
    id?: unknown
    index?: unknown
    count?: unknown
    data?: unknown
}

// The worker sends an event over Pusher's 10 KB cap as slices of its JSON (ENG-4631).
export const streamChunkJoiner = (): ((chunk: AssistantStreamChunk) => AssistantStreamEvent | null) => {
    const pending = new Map<string, Array<string | undefined>>()

    return ({id, index, count, data}) => {
        if (typeof id !== 'string' || id === '' || typeof data !== 'string'
            || typeof index !== 'number' || typeof count !== 'number'
            || !Number.isInteger(index) || !Number.isInteger(count)
            || index < 0 || index >= count) {
            return null
        }

        const parts = pending.get(id) ?? Array<string | undefined>(count).fill(undefined)
        parts[index] = data
        pending.set(id, parts)

        if (parts.some((part) => part === undefined)) {
            return null
        }

        pending.delete(id)

        try {
            const event = JSON.parse(parts.join('')) as Record<string, unknown> | null

            return isStreamEvent(event) ? event : null
        } catch {
            return null
        }
    }
}

const isStreamEvent = (value: Record<string, unknown> | null): value is Record<string, unknown> & AssistantStreamEvent =>
    typeof value?.id === 'string' && typeof value.type === 'string'

// Joins one turn's chunks and passes on a whole event of a type the panel listens for.
export const streamChunkListener = (
    types: readonly string[],
    onEvent: (event: AssistantStreamEvent) => void,
): ((chunk: AssistantStreamChunk) => void) => {
    const join = streamChunkJoiner()

    return (chunk) => {
        const event = join(chunk)

        if (event !== null && types.includes(event.type)) {
            onEvent(event)
        }
    }
}

export interface AssistantToolLine {
    id: string
    name: string
    text: string
}

export type PurchaseAction = 'resend' | 'refund'

export interface PurchaseChoice {
    saleId: string
    purchaser: string
    matched: string
    when: string
    tickets: string
    email: string
    mobile: string
    total: string
    holders: string
    event: string
    uses: string
    status: string
    action: PurchaseAction
    ticketId: string | null
    steps: TicketNextStep[]
}

export type TicketNextStepId = 'resend' | 'refund' | 'extend' | 'use' | 'link'

export interface TicketNextStep {
    id: TicketNextStepId
    label: string
    prompt: string
}

export interface TicketNextSteps {
    saleId: string
    ticketId: string | null
    steps: TicketNextStep[]
}

export interface AssistantAnswer {
    text: string
    thoughts: string
    tools: AssistantToolLine[]
    purchases: PurchaseChoice[]
    nextSteps: TicketNextSteps | null
    approvals: AssistantApproval[]
    failed: string | null
    done: boolean
}

// The approval reason includes this sentence only when noon the day before is still ahead.
export const SEND_LATER_REASON = 'Send later texts and emails them at'

// PurchaseLookup and RefundPurchase append this line, then a JSON array, when several sales match.
export const PURCHASE_CHOICES = 'PURCHASE_CHOICES'

export const NEXT_STEPS = 'NEXT_STEPS'

const NEXT_STEP_IDS: readonly TicketNextStepId[] = ['resend', 'refund', 'extend', 'use', 'link']

export const offersSendLater = (reason?: string): boolean => (reason ?? '').includes(SEND_LATER_REASON)

const SEND_NOW_NOTES = [
    'Noon the day before this tournament has passed, so the passes send when you approve.',
    'This tournament has no start time, so the passes send when you approve.',
]

export interface ApprovalWhen {
    time: string
    date: string
    zone: string
}

export interface ApprovalCopy {
    summary: string
    when: ApprovalWhen | null
    note: string | null
}

export const sendLaterSentence = (when: ApprovalWhen): string => (
    `Passes will be texted and emailed at noon on ${when.date}.`
)

// The approval reason is the file summary, then the noon sentence. The card
// keeps the summary's line breaks and shows the noon time on its own step.
export const approvalCopy = (reason?: string): ApprovalCopy => {
    const text = (reason ?? '').trim()
    const laterAt = text.indexOf(SEND_LATER_REASON)

    if (laterAt !== -1) {
        const label = text.slice(laterAt + SEND_LATER_REASON.length).trim().replace(/\.$/, '')
        const match = /^(.+?) on (.+), (.+)$/.exec(label)

        return {
            summary: text.slice(0, laterAt).trim(),
            when: match === null ? null : {time: match[1], date: match[2], zone: match[3]},
            note: match === null && label !== '' ? label : null,
        }
    }

    const note = SEND_NOW_NOTES.find((sentence) => text.endsWith(sentence)) ?? null

    return {
        summary: note === null ? text : text.slice(0, -note.length).trim(),
        when: null,
        note,
    }
}

const rejectResult = (tool: string | undefined): string => {
    if (tool === 'resend_sale') {
        return 'The tickets were not resent.'
    }

    if (tool === 'AddCoachPass') {
        return 'The extra pass was not sent.'
    }

    if (tool === 'CreateCoach') {
        return 'The coach was not added.'
    }

    if (tool === 'RefundPurchase') {
        return 'The refund was not submitted.'
    }

    if (tool === 'create_sale') {
        return 'The passes were not sent.'
    }

    if (tool === 'create_ticket') {
        return 'The pass was not added.'
    }

    if (tool === 'create_event') {
        return 'The tournament was not created.'
    }

    if (tool === 'update_event') {
        return 'The tournament was not changed.'
    }

    if (tool === 'create_ticket_type') {
        return 'The ticket was not added.'
    }

    if (tool === 'create_short_url') {
        return 'The link was not created.'
    }

    if (tool === 'update_ticket') {
        return 'The tickets were not changed.'
    }

    if (tool === 'create_validation') {
        return 'The ticket was not marked used.'
    }

    return 'The import was not approved.'
}

export const decisionLine = (
    tool: string | undefined,
    action: 'approve' | 'reject',
    sendLater = false,
    percentage?: number,
    reason?: string,
): string => {
    if (sendLater && action === 'approve') {
        return 'Send the passes at noon the day before the tournament.'
    }

    if (action === 'approve' && percentage !== undefined) {
        return `Refund ${percentage}%.`
    }

    if (action === 'approve') {
        if (tool === 'resend_sale') {
            return 'Resend the tickets.'
        }

        if (tool === 'AddCoachPass') {
            return 'Send the extra coach pass.'
        }

        if (tool === 'CreateCoach') {
            return 'Add the coach and send the pass.'
        }

        if (tool === 'RefundPurchase') {
            return 'Refund the full amount.'
        }

        if (tool === 'create_sale') {
            return 'Give the coach the passes.'
        }

        if (tool === 'create_ticket') {
            return 'Add the pass.'
        }

        if (tool === 'create_event') {
            return 'Create the tournament.'
        }

        if (tool === 'update_event') {
            return (reason ?? '').startsWith('Publish ') ? 'Publish the tournament.' : 'Save the tournament.'
        }

        if (tool === 'create_ticket_type') {
            return 'Add the ticket.'
        }

        if (tool === 'create_short_url') {
            return 'Create the link.'
        }

        if (tool === 'update_ticket') {
            return removesUsabilityExtension(reason) ? 'Remove the extension.' : 'Extend the tickets.'
        }

        if (tool === 'create_validation') {
            return 'Mark the ticket used.'
        }

        return 'Approved the import.'
    }

    if (tool === 'resend_sale') {
        return 'Do not resend the tickets.'
    }

    if (tool === 'AddCoachPass') {
        return 'Do not send the extra pass.'
    }

    if (tool === 'CreateCoach') {
        return 'Do not add the coach.'
    }

    if (tool === 'RefundPurchase') {
        return 'Do not refund.'
    }

    if (tool === 'create_sale') {
        return 'Do not send the passes.'
    }

    if (tool === 'create_ticket') {
        return 'Do not add the pass.'
    }

    if (tool === 'create_event') {
        return 'Do not create the tournament.'
    }

    if (tool === 'update_event') {
        return (reason ?? '').startsWith('Publish ') ? 'Do not publish the tournament.' : 'Do not save the tournament.'
    }

    if (tool === 'create_ticket_type') {
        return 'Do not add the ticket.'
    }

    if (tool === 'create_short_url') {
        return 'Do not create the link.'
    }

    if (tool === 'update_ticket') {
        return removesUsabilityExtension(reason) ? 'Do not remove the extension.' : 'Do not extend the tickets.'
    }

    if (tool === 'create_validation') {
        return 'Do not mark the ticket used.'
    }

    return 'Canceled the import.'
}

// One clicked card still has to answer every pending id, or the resume is rejected.
export const approvalDecisions = (
    approvals: AssistantApproval[],
    id: string,
    action: 'approve' | 'reject',
    sendLater = false,
    percentage?: number,
): Array<{id: string, action: 'approve' | 'reject', result?: string, send_later?: true, percentage?: number}> => {
    const ids = approvals
        .filter((approval) => approval.settled !== true)
        .map((approval) => approval.id)
        .filter((pendingId) => pendingId !== '')
    const pending = ids.includes(id) ? ids : [id]

    return pending.map((pendingId) => {
        const chosen = pendingId === id ? action : 'reject'
        const hold = chosen === 'approve' && sendLater
        const partial = chosen === 'approve' && percentage !== undefined
        const tool = approvals.find((approval) => approval.id === pendingId)?.tool

        return {
            id: pendingId,
            action: chosen,
            ...(chosen === 'reject' ? {result: rejectResult(tool)} : {}),
            ...(hold ? {send_later: true as const} : {}),
            ...(partial ? {percentage} : {}),
        }
    })
}

const choiceText = (value: unknown): string => typeof value === 'string' ? value : ''

const NUMBERED_PURCHASE = /^\s*\d+[.)]\s+/

// The cards sit between the count and the question. Numbered lines are the model's copy of the cards.
export const purchaseReplyParts = (text: string): {lead: string, follow: string} => {
    const lines = text
        .split('\n')
        .map((line) => line.trim())
        .filter((line) => line === '' || !NUMBERED_PURCHASE.test(line))

    const compact: string[] = []

    lines.forEach((line) => {
        if (line === '' && (compact.length === 0 || compact[compact.length - 1] === '')) {
            return
        }

        compact.push(line)
    })

    while (compact[compact.length - 1] === '') {
        compact.pop()
    }

    const followAt = compact.findIndex((line) => /^(which|what)\b/i.test(line))
    const before = followAt > 0 ? compact.slice(0, followAt) : compact
    const matchAt = before.findIndex((line) => /purchases? match\b/i.test(line))
    const leadLines = matchAt > 0
        ? [before[matchAt], ...before.filter((_, index) => index !== matchAt)]
        : before

    return {
        lead: leadLines.join('\n').trim(),
        follow: followAt > 0 ? compact.slice(followAt).join('\n').trim() : '',
    }
}

// The tool result is hidden. The array after the marker is the selectable list.
export const purchaseChoices = (toolName: string, text: string): PurchaseChoice[] => {
    if (toolName !== 'PurchaseLookup' && toolName !== 'RefundPurchase') {
        return []
    }

    const action: PurchaseAction = toolName === 'RefundPurchase' ? 'refund' : 'resend'

    const marker = `\n${PURCHASE_CHOICES}\n`
    const at = text.lastIndexOf(marker)

    if (at === -1) {
        return []
    }

    try {
        const parsed = JSON.parse(text.slice(at + marker.length)) as unknown

        if (!Array.isArray(parsed)) {
            return []
        }

        return parsed.flatMap((row): PurchaseChoice[] => {
            if (row === null || typeof row !== 'object') {
                return []
            }

            const choice = row as Record<string, unknown>
            const saleId = choiceText(choice.sale_id)
            const purchaser = choiceText(choice.purchaser)

            if (saleId === '' || purchaser === '') {
                return []
            }

            return [{
                saleId,
                purchaser,
                matched: choiceText(choice.matched),
                when: choiceText(choice.when),
                tickets: choiceText(choice.tickets),
                email: choiceText(choice.email),
                mobile: choiceText(choice.mobile),
                total: choiceText(choice.total),
                holders: choiceText(choice.holders),
                event: choiceText(choice.event),
                uses: choiceText(choice.uses),
                status: choiceText(choice.status),
                action,
                ticketId: choiceText(choice.ticket_id) || null,
                steps: choiceSteps(choice.steps),
            }]
        })
    } catch {
        return []
    }
}

const choiceSteps = (value: unknown): TicketNextStep[] => {
    if (!Array.isArray(value)) {
        return []
    }

    return value.flatMap((row): TicketNextStep[] => {
        if (row === null || typeof row !== 'object') {
            return []
        }

        const step = row as Record<string, unknown>
        const id = choiceText(step.id)
        const label = choiceText(step.label)
        const prompt = choiceText(step.prompt)

        if (!NEXT_STEP_IDS.includes(id as TicketNextStepId) || label === '' || prompt === '') {
            return []
        }

        return [{id: id as TicketNextStepId, label, prompt}]
    })
}

// One purchase lookup appends this object. The buttons are the steps. The tool text stays hidden.
export const ticketNextSteps = (toolName: string, text: string): TicketNextSteps | null => {
    if (toolName !== 'PurchaseLookup') {
        return null
    }

    const marker = `\n${NEXT_STEPS}\n`
    const at = text.lastIndexOf(marker)

    if (at === -1) {
        return null
    }

    try {
        const parsed = JSON.parse(text.slice(at + marker.length)) as unknown

        if (parsed === null || typeof parsed !== 'object' || Array.isArray(parsed)) {
            return null
        }

        const record = parsed as Record<string, unknown>
        const saleId = choiceText(record.sale_id)

        if (saleId === '') {
            return null
        }

        const ticketId = choiceText(record.ticket_id)
        const steps = choiceSteps(record.steps)

        if (steps.length === 0) {
            return null
        }

        return {
            saleId,
            ticketId: ticketId === '' ? null : ticketId,
            steps,
        }
    } catch {
        return null
    }
}

// The preview endpoint returns the first rows. Drop anything that is not short text.
export const previewRows = (value: unknown): string[][] => {
    if (!Array.isArray(value)) {
        return []
    }

    return value.flatMap((row): string[][] => {
        if (!Array.isArray(row) || row.some((cell) => typeof cell !== 'string')) {
            return []
        }

        const cells = row.slice(0, 4)

        return cells.some((cell) => cell !== '') ? [cells] : []
    }).slice(0, 4)
}

const PREVIEW_CONFIRM = 'Preview only. You\'ll confirm before anything is sent.'

// A count the preview endpoint can be trusted to draw. Anything else is zero.
export const previewCount = (value: unknown): number => {
    if (typeof value !== 'number' || !Number.isInteger(value) || value < 0) {
        return 0
    }

    return Math.min(value, 1_000_000)
}

// The line under a clipped table. A scan that stopped early is a lower bound.
export const moreRowsLabel = (remaining: number, capped: boolean): string | null => {
    if (!Number.isInteger(remaining) || remaining <= 0) {
        return null
    }

    const count = remaining.toLocaleString('en-US')

    return capped ? `…and at least ${count} more` : `…and ${count} more`
}

export const COACH_FILE_EXTENSIONS = ['xlsx', 'xls', 'csv', 'txt', 'tsv', 'docx', 'doc'] as const

export const COACH_FILE_ACCEPT = '.xlsx,.xls,.csv,.txt,.tsv,.docx,.doc'

export const coachUploadPrompt = 'Upload coach passes'

const coachFileExtensions = COACH_FILE_EXTENSIONS.map((extension) => `.${extension}`)

export const coachUploadReply = `Please attach the coach spreadsheet (${coachFileExtensions.slice(0, -1).join(', ')}, or ${coachFileExtensions.at(-1)}); I\u2019ll show you what it contains before anything is created.\n\nNeed a template you can fill out and upload?`

const COACH_FILE_TYPE_LIST = 'xlsx, xls, csv, txt, tsv, docx, or doc'

const coachExtensions = new Set<string>(COACH_FILE_EXTENSIONS)

// The last suffix, so notes.PDF and a path both resolve to pdf. A name with no dot has none.
const coachFileExtension = (name: string): string => {
    const base = name.trim().split(/[/\\]/).pop() ?? ''
    const parts = base.split('.')

    if (parts.length < 2) {
        return ''
    }

    return parts.pop()?.toLowerCase() ?? ''
}

// The type badge on an attached file's tile, e.g. CSV. Empty when the name has no extension.
export const fileTypeBadge = (name: string): string => coachFileExtension(name).toUpperCase()

// Null means the file can be attached. The sentence names the file and the allowed types.
export const coachFileRefusal = (name: string): string | null => {
    const extension = coachFileExtension(name)

    if (coachExtensions.has(extension)) {
        return null
    }

    const label = name.trim() === '' ? 'That file' : name.trim()
    const why = extension === ''
        ? 'it has no file extension'
        : `.${extension} is not a supported file type`

    return `${label} was not attached because ${why}. Use ${COACH_FILE_TYPE_LIST}.`
}

// The first dropped name wins. A closed composer, a busy turn, or an empty drop changes nothing.
export const composerDrop = ({
    acceptsFile,
    canReceive,
    names,
}: {
    acceptsFile: boolean
    canReceive: boolean
    names: readonly string[]
}): {action: 'ignore'} | {action: 'attach'} | {action: 'refuse', message: string} => {
    const name = names[0]

    if (!acceptsFile || !canReceive || name === undefined) {
        return {action: 'ignore'}
    }

    const message = coachFileRefusal(name)

    if (message !== null) {
        return {action: 'refuse', message}
    }

    return {action: 'attach'}
}

export const attachmentStatusText = (
    fileName: string | null,
    previewNote: string | null,
    rowCount: number,
    removalNote: string,
    remaining: number,
    capped: boolean,
    refusal = '',
): string => {
    if (refusal !== '') {
        return refusal
    }

    if (fileName === null) {
        return removalNote
    }

    const more = moreRowsLabel(remaining, capped)
    const tail = more === null ? PREVIEW_CONFIRM : `${more}. ${PREVIEW_CONFIRM}`

    if (previewNote === 'Reading the file...') {
        return `Reading the file... ${fileName}. ${tail}`
    }

    if (previewNote) {
        return `${previewNote} ${tail}`
    }

    if (rowCount === 0) {
        return tail
    }

    const ready = `Preview ready, ${rowCount === 1 ? '1 row' : `${rowCount} rows`}.`

    return `${ready} ${tail}`
}

export interface RecentConversation {
    id: string
    title: string
    updatedAt: string
    eventId: string | null
}

export interface StoredThreadMessage {
    id: string
    role: 'user' | 'assistant'
    text: string
    attachmentName: string | null
    tools: AssistantToolLine[]
    purchases: PurchaseChoice[]
    nextSteps: TicketNextSteps | null
    approvals: AssistantApproval[]
}

const RECENT_LIMIT = 5

export const conversationTitle = (title: string): string => {
    const trimmed = title.trim()

    return trimmed === '' ? 'Untitled chat' : trimmed
}

export const conversationWhen = (iso: string, timeZone?: string): string => {
    const date = new Date(iso)

    if (Number.isNaN(date.getTime())) {
        return ''
    }

    return new Intl.DateTimeFormat('en-US', {
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        timeZone,
    }).format(date)
}

// The server already limits and orders the rows. Sort again so a stale
// payload still shows the newest five first.
export const recentConversations = (value: unknown): RecentConversation[] => {
    if (!Array.isArray(value)) {
        return []
    }

    const rows = value.flatMap((row): RecentConversation[] => {
        if (row === null || typeof row !== 'object') {
            return []
        }

        const record = row as Record<string, unknown>
        const id = typeof record.id === 'string' ? record.id : ''
        const updatedAt = typeof record.updated_at === 'string' ? record.updated_at : ''

        if (id === '' || updatedAt === '') {
            return []
        }

        return [{
            id,
            title: typeof record.title === 'string' ? record.title : '',
            updatedAt,
            eventId: typeof record.event_id === 'string' && record.event_id !== '' ? record.event_id : null,
        }]
    })

    return rows
        .sort((left, right) => {
            if (left.updatedAt !== right.updatedAt) {
                return left.updatedAt < right.updatedAt ? 1 : -1
            }

            if (left.id === right.id) {
                return 0
            }

            return left.id < right.id ? 1 : -1
        })
        .slice(0, RECENT_LIMIT)
}

const storedTools = (value: unknown): AssistantToolLine[] => {
    if (!Array.isArray(value)) {
        return []
    }

    return value.flatMap((row): AssistantToolLine[] => {
        if (row === null || typeof row !== 'object') {
            return []
        }

        const record = row as Record<string, unknown>
        const id = typeof record.id === 'string' ? record.id : ''

        if (id === '') {
            return []
        }

        return [{
            id,
            name: typeof record.name === 'string' && record.name !== '' ? record.name : 'Tool',
            text: typeof record.text === 'string' ? record.text : '',
        }]
    })
}

const storedApprovals = (value: unknown): AssistantApproval[] => {
    if (!Array.isArray(value)) {
        return []
    }

    return value.flatMap((row): AssistantApproval[] => {
        if (row === null || typeof row !== 'object') {
            return []
        }

        const record = row as Record<string, unknown>

        if (typeof record.id !== 'string' || record.id === '') {
            return []
        }

        return [{
            id: record.id,
            tool: typeof record.tool === 'string' ? record.tool : undefined,
            reason: typeof record.reason === 'string' ? record.reason : undefined,
        }]
    })
}

// A stored thread is the user text, the assistant text, a file name, purchase
// cards, and an approval that is still waiting. Settled approvals stay in the
// later reply instead of coming back as a card.
export const storedThread = (value: unknown): StoredThreadMessage[] => {
    if (!Array.isArray(value)) {
        return []
    }

    return value.flatMap((row): StoredThreadMessage[] => {
        if (row === null || typeof row !== 'object') {
            return []
        }

        const record = row as Record<string, unknown>
        const id = typeof record.id === 'string' ? record.id : ''
        const role = record.role === 'user' || record.role === 'assistant' ? record.role : null

        if (id === '' || role === null) {
            return []
        }

        const text = typeof record.text === 'string' ? record.text : ''
        const attachmentName = typeof record.attachment_name === 'string' && record.attachment_name !== ''
            ? record.attachment_name
            : null
        const tools = storedTools(record.tools)
        const seenSales = new Set<string>()
        const purchases = tools.flatMap((tool) => purchaseChoices(tool.name, tool.text))
            .filter((purchase) => {
                if (seenSales.has(purchase.saleId)) {
                    return false
                }

                seenSales.add(purchase.saleId)

                return true
            })
        let nextSteps: TicketNextSteps | null = null
        tools.forEach((tool) => {
            if (tool.name === 'PurchaseLookup') {
                nextSteps = ticketNextSteps(tool.name, tool.text)
            }
        })
        const approvals = storedApprovals(record.approvals)
        const visible = text !== '' || attachmentName !== null || purchases.length > 0 || nextSteps !== null || approvals.length > 0

        if (!visible) {
            return []
        }

        return [{
            id,
            role,
            text,
            attachmentName,
            tools,
            purchases,
            nextSteps,
            approvals,
        }]
    })
}

export const emptyAnswer = (): AssistantAnswer => ({
    text: '',
    thoughts: '',
    tools: [],
    purchases: [],
    nextSteps: null,
    approvals: [],
    failed: null,
    done: false,
})

const formatResult = (result: unknown): string => {
    if (typeof result === 'string') {
        return result
    }

    if (result === undefined || result === null) {
        return ''
    }

    return JSON.stringify(result)
}

export interface TextPart {
    text: string
    href?: string
}

const URL_IN_TEXT = /https?:\/\/[^\s<>"']+/g
const TRAILING_URL_PUNCTUATION = /[.,;:!?)]+$/

export const linkParts = (value: string): TextPart[] => {
    const parts: TextPart[] = []
    let cursor = 0

    for (const match of value.matchAll(URL_IN_TEXT)) {
        const start = match.index ?? 0
        const trimmed = match[0].replace(TRAILING_URL_PUNCTUATION, '')

        if (trimmed.endsWith('//')) {
            continue
        }

        if (start > cursor) {
            parts.push({text: value.slice(cursor, start)})
        }

        parts.push({text: trimmed, href: trimmed})
        cursor = start + trimmed.length
    }

    if (cursor < value.length || parts.length === 0) {
        parts.push({text: value.slice(cursor)})
    }

    return parts
}

const WRITE_OUTCOME = new Set(['create_event', 'update_event', 'create_ticket_type', 'create_short_url', 'update_ticket', 'create_validation', 'resend_sale'])

// The server's update_ticket card for a null end starts "Remove the extension".
export const removesUsabilityExtension = (reason?: string): boolean => (reason ?? '').startsWith('Remove ')

const usabilityOutcome = (record: Record<string, unknown>): string | null => {
    const tickets = Array.isArray(record.tickets) ? record.tickets : []

    if (tickets.length === 0) {
        return null
    }

    const noun = tickets.length === 1 ? '1 ticket' : `${tickets.length.toLocaleString('en-US')} tickets`
    const cleared = tickets.every((ticket) => ticket !== null && typeof ticket === 'object' && (ticket as Record<string, unknown>).usable_until === null)

    return cleared ? `Removed the extension on ${noun}.` : `Extended the use window on ${noun}.`
}

const validationOutcome = (record: Record<string, unknown>): string | null => {
    const validation = record.validation as Record<string, unknown> | null | undefined
    const ticket = record.ticket as Record<string, unknown> | null | undefined
    const ticketType = ticket?.ticket_type as Record<string, unknown> | null | undefined

    if (validation?.status !== 'valid') {
        return null
    }

    return typeof ticketType?.name === 'string' && ticketType.name !== ''
        ? `Marked the ${ticketType.name} ticket used.`
        : 'Marked the ticket used.'
}

const resendOutcome = (record: Record<string, unknown>): string | null => {
    const sent = [
        typeof record.emailed_to === 'string' && record.emailed_to !== '' ? `emailed to ${record.emailed_to}` : null,
        typeof record.texted_to === 'string' && record.texted_to !== '' ? `texted to ${record.texted_to}` : null,
    ].filter((part): part is string => part !== null)

    return sent.length === 0
        ? null
        : record.replayed === true
            ? `The tickets link was already ${sent.join(' and ')} in the last 10 minutes. Nothing was sent again.`
            : `The tickets link was ${sent.join(' and ')}.`
}

// A tool result that is one JSON object, or null.
const resultRecord = (text: string): Record<string, unknown> | null => {
    let parsed: unknown

    try {
        parsed = JSON.parse(text)
    } catch {
        return null
    }

    return parsed !== null && typeof parsed === 'object' && !Array.isArray(parsed)
        ? parsed as Record<string, unknown>
        : null
}

const outcomeSentence = (name: string, text: string): string | null => {
    if (!WRITE_OUTCOME.has(name)) {
        return null
    }

    const record = resultRecord(text)

    if (record === null) {
        return null
    }

    if (name === 'update_ticket') {
        return usabilityOutcome(record)
    }

    if (name === 'create_validation') {
        return validationOutcome(record)
    }

    if (name === 'resend_sale') {
        return resendOutcome(record)
    }

    const label = typeof record.name === 'string' ? record.name : ''
    const state = typeof record.state === 'string' ? record.state : ''
    const url = typeof record.url === 'string' ? record.url : ''

    if (name === 'create_short_url') {
        return url === '' ? null : `Short link: ${url}`
    }

    if (name === 'create_ticket_type') {
        return label === '' ? null : `Added ${label}.`
    }

    if (label === '') {
        return null
    }

    if (name === 'create_event') {
        const sentence = state === 'live'
            ? `Created ${label} and put it on sale.`
            : `Created ${label} as a draft.`

        return url === '' ? sentence : `${sentence} ${url}`
    }

    const sentence = state === 'live'
        ? `${label} is on sale.`
        : state === 'draft'
            ? `${label} is a draft.`
            : state === 'complete'
                ? `${label} is complete.`
                : `Saved ${label}.`

    return url === '' ? sentence : `${sentence} ${url}`
}

const COPIED_LINE = 'Copied to your clipboard.'

// A sale's link comes back without destination_url. Only that link is copied (ENG-4664).
export const saleLinkUrl = (event: AssistantStreamEvent): string | null => {
    if (event.type !== 'tool_result' || event.tool_name !== 'create_short_url' || event.successful === false) {
        return null
    }

    const record = resultRecord(formatResult(event.result))

    if (record === null || 'destination_url' in record) {
        return null
    }

    return typeof record.url === 'string' && record.url !== '' ? record.url : null
}

// The line is added only after the copy worked, so a blocked clipboard claims nothing.
export const withCopiedLine = (text: string, copied: boolean): string => copied && text.trim() !== ''
    ? `${text.trimEnd()}\n${COPIED_LINE}`
    : text

const outcomeText = (tools: {name: string, text: string}[]): string => tools.flatMap((tool) => {
    const sentence = outcomeSentence(tool.name, tool.text)

    return sentence === null ? [] : [sentence]
}).join('\n')

export const applyAssistantEvent = (answer: AssistantAnswer, event: AssistantStreamEvent): AssistantAnswer => {
    if (event.type === 'text_delta') {
        return {
            ...answer,
            text: answer.text + (event.delta ?? ''),
        }
    }

    if (event.type === 'reasoning_delta') {
        return {
            ...answer,
            thoughts: answer.thoughts + (event.delta ?? ''),
        }
    }

    if (event.type === 'tool_result') {
        const text = event.successful === false
            ? (event.error ?? 'The tool failed.')
            : formatResult(event.result)

        const name = event.tool_name ?? 'Tool'
        const incoming = purchaseChoices(name, text)
        const seenSales = new Set(answer.purchases.map((purchase) => purchase.saleId))

        return {
            ...answer,
            tools: [
                ...answer.tools,
                {
                    id: event.id,
                    name,
                    text,
                },
            ],
            purchases: [
                ...answer.purchases,
                ...incoming.filter((purchase) => !seenSales.has(purchase.saleId)),
            ],
            nextSteps: name === 'PurchaseLookup' ? ticketNextSteps(name, text) : answer.nextSteps,
        }
    }

    if (event.type === 'tool_approval_request') {
        const approvals = (event.approvals ?? []).flatMap((approval): AssistantApproval[] => {
            if (typeof approval.id !== 'string' || approval.id === '') {
                return []
            }

            return [{
                id: approval.id,
                tool: typeof approval.tool === 'string' ? approval.tool : undefined,
                reason: typeof approval.reason === 'string' ? approval.reason : undefined,
            }]
        })

        return {
            ...answer,
            approvals,
        }
    }

    if (event.type === 'stream_end') {
        return {
            ...answer,
            text: answer.text.trim() === '' ? outcomeText(answer.tools) : answer.text,
            done: true,
        }
    }

    if (event.type === 'stream_failed') {
        return {
            ...answer,
            failed: event.message ?? 'The stream failed.',
            done: true,
        }
    }

    return answer
}

export interface FigureRow {
    label: string
    value: string
}

export interface FigureBlock {
    title: string
    note: string
    rows: FigureRow[]
}

export interface FigureReport {
    blocks: FigureBlock[]
    lines: string[]
}

const FIGURE_LABEL = /^([^:]+):\s*(.+)$/
const MONEY_AMOUNT = /(-?[\d,]+) cents \((-\$[\d,]+\.\d{2}|\$[\d,]+\.\d{2})\)/
const COUNT_AMOUNT = /^-?[\d,]+$/
const EVENT_ID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
const EVENT_START = /^\d{4}-\d{2}-\d{2}T/
const COMPARE_HEAD = /^(This year|Last year|Difference \(this year minus last year\)) \| /
const UNAVAILABLE = new Set([
    'Tickets, gross, and net are not available yet.',
    'Tickets and gross are not available yet.',
])

const moneyAmount = (raw: string): string | null => {
    const match = MONEY_AMOUNT.exec(raw)

    return match === null ? null : match[2]
}

const countAmount = (raw: string): string | null => {
    const value = raw.trim().replace(/\.$/, '')

    if (value === 'unset' || COUNT_AMOUNT.test(value)) {
        return value
    }

    return null
}

const labeledRows = (text: string, labels: readonly string[], moneyLabels: readonly string[]): FigureRow[] => {
    const wanted = new Set(labels)
    const money = new Set(moneyLabels)
    const rows: FigureRow[] = []

    text.split('\n').forEach((line) => {
        const match = FIGURE_LABEL.exec(line.trim())

        if (match === null || !wanted.has(match[1])) {
            return
        }

        const value = money.has(match[1]) ? moneyAmount(match[2]) : countAmount(match[2])

        if (value !== null) {
            rows.push({label: match[1], value})
        }
    })

    return rows
}

const flatReport = (rows: FigureRow[]): FigureReport | null => (
    rows.length === 0 ? null : {blocks: [{title: '', note: '', rows}], lines: []}
)

const compareReport = (text: string): FigureReport | null => {
    const blocks: FigureBlock[] = []

    text.split('\n').forEach((line) => {
        if (!COMPARE_HEAD.test(line)) {
            return
        }

        const parts = line.split(' | ').map((part) => part.trim())
        const title = parts[0].startsWith('Difference') ? 'Difference' : parts[0]
        const rest = EVENT_ID.test(parts[1] ?? '') ? parts.slice(5) : parts.slice(1)
        let note = ''
        const rows: FigureRow[] = []

        rest.forEach((part) => {
            const through = /^Through (.+)$/.exec(part)

            if (through !== null) {
                note = `Through ${through[1]}`

                return
            }

            if (UNAVAILABLE.has(part)) {
                return
            }

            const labeled = FIGURE_LABEL.exec(part)

            if (labeled === null) {
                return
            }

            const value = labeled[1] === 'Gross' || labeled[1] === 'Net'
                ? moneyAmount(labeled[2])
                : countAmount(labeled[2])

            if (value !== null) {
                rows.push({label: labeled[1], value})
            }
        })

        if (rows.length > 0 || note !== '') {
            blocks.push({title, note, rows})
        }
    })

    return blocks.length === 0 ? null : {blocks, lines: []}
}

interface TicketTypeRow {
    eventId: string
    start: string
    name: string
    count: string
    base: string
}

const ticketReport = (text: string): FigureReport | null => {
    const events: Array<{id: string, start: string, rows: FigureRow[]}> = []
    let baseTotal: string | null = null

    text.split('\n').forEach((line) => {
        const total = /^Base total:\s*(.+)$/.exec(line.trim())

        if (total !== null) {
            baseTotal = moneyAmount(total[1])

            return
        }

        const parts = line.split(' | ').map((part) => part.trim())
        const count = parts[5]
        const base = parts[6] === undefined ? null : moneyAmount(parts[6])

        if (!EVENT_ID.test(parts[0] ?? '') || count === undefined || !COUNT_AMOUNT.test(count) || base === null) {
            return
        }

        const row: TicketTypeRow = {
            eventId: parts[0],
            start: parts[2] ?? '',
            name: parts[4] ?? '',
            count,
            base,
        }
        const existing = events.find((event) => event.id === row.eventId)

        if (existing === undefined) {
            events.push({
                id: row.eventId,
                start: row.start,
                rows: [{label: row.name, value: `${row.count} · ${row.base}`}],
            })

            return
        }

        existing.rows.push({label: row.name, value: `${row.count} · ${row.base}`})
    })

    const current = events[0]
    const previous = events[1]

    if (current === undefined) {
        return null
    }

    // This year is emitted first, so its start is the later one. A last-few-weeks window is chronological.
    const blocks: FigureBlock[] = []

    if (
        events.length === 2
        && previous !== undefined
        && EVENT_START.test(current.start)
        && EVENT_START.test(previous.start)
        && current.start >= previous.start
    ) {
        blocks.push({title: 'This year', note: '', rows: current.rows})
        blocks.push({title: 'Last year', note: '', rows: previous.rows})
    } else {
        blocks.push({title: '', note: '', rows: events.flatMap((event) => event.rows)})
    }

    if (baseTotal !== null) {
        blocks.push({title: '', note: '', rows: [{label: 'Base total', value: baseTotal}]})
    }

    return {blocks, lines: []}
}

const coachReport = (text: string): FigureReport | null => {
    if (text.includes('\nCOACH_CHOICES\n') || text.split('\n').some((line) => line.trim() === 'Which coach?')) {
        return null
    }

    const lines = text.split('\n').map((line) => line.trim()).filter((line) => line !== '')

    return lines.length < 2 ? null : {blocks: [], lines}
}

const groupedCount = (value: number): string => {
    const negative = value < 0
    const text = String(Math.abs(Math.trunc(value))).replace(/\B(?=(\d{3})+(?!\d))/g, ',')

    return negative ? `-${text}` : text
}

const dollars = (cents: number): string => {
    const negative = cents < 0
    const absolute = Math.abs(Math.trunc(cents))
    const text = `$${groupedCount(Math.trunc(absolute / 100))}.${String(absolute % 100).padStart(2, '0')}`

    return negative ? `-${text}` : text
}

const whole = (value: unknown): number | null => (
    typeof value === 'number' && Number.isFinite(value) ? Math.trunc(value) : null
)

const centsOf = (value: unknown): number | null => {
    if (typeof value !== 'object' || value === null || !('amount' in value)) {
        return null
    }

    return whole((value as {amount?: unknown}).amount)
}

const channelRows = (bucket: unknown): FigureRow[] => {
    if (typeof bucket !== 'object' || bucket === null) {
        return []
    }

    const agent = (bucket as {agent_assisted?: {total?: unknown, tap_to_pay?: unknown}}).agent_assisted
    const selfServed = (bucket as {self_served?: {total?: unknown, apple_pay?: unknown}}).self_served
    const rows: FigureRow[] = []
    const agentTotal = whole(agent?.total)
    const tapToPay = whole(agent?.tap_to_pay)
    const selfTotal = whole(selfServed?.total)
    const applePay = whole(selfServed?.apple_pay)

    if (agentTotal !== null) {
        rows.push({label: 'Agent assisted', value: groupedCount(agentTotal)})
    }

    if (tapToPay !== null) {
        rows.push({label: 'Tap to Pay', value: groupedCount(tapToPay)})
    }

    if (selfTotal !== null) {
        rows.push({label: 'Self served', value: groupedCount(selfTotal)})
    }

    if (applePay !== null) {
        rows.push({label: 'Apple Pay', value: groupedCount(applePay)})
    }

    return rows
}

const statisticsReport = (text: string): FigureReport | null => {
    let parsed: unknown

    try {
        parsed = JSON.parse(text)
    } catch {
        return null
    }

    if (typeof parsed !== 'object' || parsed === null || !('totals' in parsed)) {
        return null
    }

    const body = parsed as {
        totals?: unknown,
        sources?: {sales?: unknown},
        locations?: unknown,
        ticket_types?: unknown,
    }
    const totals = body.totals

    if (typeof totals !== 'object' || totals === null) {
        return null
    }

    const record = totals as Record<string, unknown>
    const rows: FigureRow[] = []
    const sales = whole(record.sales)
    const tickets = whole(record.tickets_sold)
    const coaches = whole(record.coaches_sent)
    const gross = centsOf(record.gross_revenue)
    const net = centsOf(record.net_revenue)
    const refunds = centsOf(record.refunds_total)

    if (sales !== null) {
        rows.push({label: 'Sales', value: groupedCount(sales)})
    }

    if (tickets !== null) {
        rows.push({label: 'Tickets', value: groupedCount(tickets)})
    }

    if (coaches !== null && coaches !== 0) {
        rows.push({label: 'Coach passes', value: groupedCount(coaches)})
    }

    if (gross !== null) {
        rows.push({label: 'Gross', value: dollars(gross)})
    }

    if (net !== null) {
        rows.push({label: 'Net', value: dollars(net)})
    }

    if (refunds !== null && refunds !== 0) {
        rows.push({label: 'Refunds', value: dollars(refunds)})
    }

    const blocks: FigureBlock[] = []

    if (rows.length > 0) {
        blocks.push({title: '', note: '', rows})
    }

    const paid = channelRows(body.sources?.sales)

    if (paid.length > 0) {
        blocks.push({title: 'How they paid', note: '', rows: paid})
    }

    const venues = Array.isArray(body.locations) ? body.locations.flatMap((location): FigureRow[] => {
        if (typeof location !== 'object' || location === null) {
            return []
        }

        const count = whole((location as {item_count?: unknown}).item_count)

        if (count === null) {
            return []
        }

        const name = (location as {location_name?: unknown}).location_name
        const label = typeof name === 'string' && name !== '' && name !== 'No Location' ? name : 'No location'
        const tax = centsOf((location as {tax?: unknown}).tax)
        const value = tax === null ? groupedCount(count) : `${groupedCount(count)} · ${dollars(tax)} tax`

        return [{label, value}]
    }) : []

    if (venues.length > 0) {
        blocks.push({title: 'Venues', note: '', rows: venues})
    }

    const types = Array.isArray(body.ticket_types) ? body.ticket_types.flatMap((type): FigureRow[] => {
        if (typeof type !== 'object' || type === null) {
            return []
        }

        const count = whole((type as {item_count?: unknown}).item_count)
        const name = (type as {name?: unknown}).name

        if (count === null || typeof name !== 'string' || name === '') {
            return []
        }

        return [{label: name, value: groupedCount(count)}]
    }) : []

    if (types.length > 0) {
        blocks.push({title: 'Ticket types', note: '', rows: types})
    }

    return blocks.length === 0 ? null : {blocks, lines: []}
}

const figuresForTool = (name: string, text: string): FigureReport | null => {
    if (name === 'get_event_statistics') {
        return statisticsReport(text)
    }

    if (name === 'EventMoney') {
        return flatReport(labeledRows(text, ['Tickets', 'Gross', 'Net', 'Registrations collected'], ['Gross', 'Net', 'Registrations collected']))
    }

    if (name === 'OrganizationRevenue') {
        return flatReport(labeledRows(text, ['Gross', 'Net'], ['Gross', 'Net']))
    }

    if (name === 'TeamCount') {
        return flatReport(labeledRows(text, ['Team Count', 'Registered teams'], []))
    }

    if (name === 'Compare') {
        return compareReport(text)
    }

    if (name === 'TicketSales') {
        return ticketReport(text)
    }

    if (name === 'CoachPassAssignments') {
        return coachReport(text)
    }

    return null
}

// The latest figure-bearing tool wins. Purchase cards and approval text are not figures.
export const figureReport = (tools: AssistantToolLine[]): FigureReport | null => {
    let report: FigureReport | null = null

    tools.forEach((tool) => {
        const parsed = figuresForTool(tool.name, tool.text)

        if (parsed !== null) {
            report = parsed
        }
    })

    return report
}

// Hidden tool text is not something to paint. A finished coach-import line has
// none once its card is cleared, and that empty pill is the blank line (ENG-4505).
export const showsTranscriptBubble = (message: {
    role: 'user' | 'assistant'
    text: string
    done: boolean
    failed: string | null
    attachmentName?: string | null
    rows?: readonly string[][]
    purchases: readonly unknown[]
    nextSteps?: TicketNextSteps | null
    approvals: readonly unknown[]
    tools: readonly AssistantToolLine[]
}): boolean => {
    if (message.text !== '' || (message.attachmentName ?? '') !== '' || (message.rows?.length ?? 0) > 0) {
        return true
    }

    if (message.purchases.length > 0 || (message.nextSteps?.steps.length ?? 0) > 0 || message.approvals.length > 0 || (message.failed ?? '') !== '') {
        return true
    }

    if (message.role !== 'assistant') {
        return false
    }

    return !message.done || figureReport([...message.tools]) !== null
}

export const applyAssistantEvents = (
    answer: AssistantAnswer,
    events: AssistantStreamEvent[],
    seen: Set<string>,
): AssistantAnswer => events.reduce((current, event) => {
    if (!event.id || seen.has(event.id)) {
        return current
    }

    seen.add(event.id)

    return applyAssistantEvent(current, event)
}, answer)
